import { FC, useMemo, useRef, useState } from "react"
import clsx from "clsx"

import { useProps } from "contexts/PropsContext"

import { useClickOutside } from "hooks/useClickOutside"

type Option = { name: string; value: string; disable?: boolean }

type Props = {
  title?: string
  placeholder?: string
  options: Option[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  rounded?: boolean
  searchable?: boolean
  maxLabelItems?: number
  panelLabel?: string
}

export const TagsMultiSelect: FC<Props> = ({
  title,
  placeholder = "All tags",
  options,
  value,
  onChange,
  disabled = false,
  rounded = true,
  searchable = true,
  maxLabelItems = 2,
  panelLabel = "Tags"
}) => {
  const { darkMode } = useProps()

  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const rootRef = useRef<HTMLDivElement | null>(null)
  useClickOutside(rootRef, () => setOpen(false), open)

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return options
    return options.filter((o) => o.name.toLowerCase().includes(term))
  }, [options, q])

  const selectedLabel = useMemo(() => {
    if (!value?.length) return placeholder
    const selectedNames = options.filter((o) => value.includes(o.value)).map((o) => o.name)
    if (selectedNames.length <= maxLabelItems) return selectedNames.join(", ")
    return `${selectedNames.length} selected`
  }, [value, options, placeholder, maxLabelItems])

  const toggleOne = (id: string) => {
    if (disabled) return
    if (value.includes(id)) onChange(value.filter((x) => x !== id))
    else onChange([...value, id])
  }

  const clear = () => onChange([])

  const baseBtn = clsx(
    "select select-bordered outline-none px-[5px] w-full flex items-center justify-between cursor-pointer",
    rounded && "rounded-full items-center text-left pl-[24px] appearance-none",
    {
      "bg-[#0C0C1A] text-white border-[rgba(255,255,255,0.1)]": darkMode && rounded,
      "bg-[#F5F5F5] text-black border-gray-200": !darkMode && rounded,
      "opacity-60 cursor-not-allowed": disabled
    }
  )

  const panel = clsx(
    "absolute left-0 top-[calc(100%+8px)] w-full z-50 rounded-xl border shadow-lg",
    darkMode ? "bg-[#0C0C1A] border-[rgba(255,255,255,0.1)]" : "bg-white border-gray-200"
  )

  return (
    <div ref={rootRef} className="w-full form-control relative  min-w-[200px]">
      {Boolean(title) && (
        <label className="label">
          <span className="label-text">{title}</span>
        </label>
      )}

      <button type="button" className={baseBtn} onClick={() => !disabled && setOpen((v) => !v)} aria-expanded={open}>
        <span className="truncate pr-6">{selectedLabel}</span>
        <span className="absolute right-4 opacity-70">▾</span>
      </button>

      {open && (
        <div className={panel}>
          <div className="p-3 border-b border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm opacity-80">{panelLabel}</div>
              <button type="button" className={clsx("text-sm underline", disabled && "opacity-60 pointer-events-none")} onClick={clear}>
                Clear
              </button>
            </div>

            {searchable && (
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className={clsx("mt-2 input input-bordered w-full", darkMode ? "bg-[#0C0C1A] border-[rgba(255,255,255,0.1)]" : "bg-white")}
              />
            )}
          </div>

          <div className="max-h-64 overflow-auto p-2">
            {filtered.length === 0 ? (
              <div className="p-2 text-sm opacity-70">No tags found</div>
            ) : (
              filtered.map((opt) => {
                const checked = value.includes(opt.value)
                return (
                  <label
                    key={opt.value}
                    className={clsx(
                      "flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer",
                      darkMode ? "hover:bg-[rgba(255,255,255,0.06)]" : "hover:bg-gray-100",
                      (disabled || opt.disable) && "opacity-60 cursor-not-allowed"
                    )}
                  >
                    <input type="checkbox" checked={checked} disabled={disabled || opt.disable} onChange={() => toggleOne(opt.value)} />
                    <span className="text-sm">{opt.name}</span>
                  </label>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
